const SDK_NAME = "semparse-sdk-core";
const SDK_VERSION = "0.0.0";
const DEFAULT_SDK = { name: SDK_NAME, version: SDK_VERSION } as const;
const MAX_EXCEPTION_TYPE_LENGTH = 128;
const MAX_EXCEPTION_VALUE_LENGTH = 1024;
const MAX_STACKTRACE_INPUT_LENGTH = 8192;
const MAX_STACKTRACE_FRAMES = 40;
const MAX_STACKTRACE_STRING_FIELD_LENGTH = 1024;
const TRUNCATION_SUFFIX = "…[truncated]";

/** Allowed severity levels for captured events. */
export type SemparseSeverity = "fatal" | "error" | "warning" | "info" | "debug";

/** User identity fields attached to an event. */
export type SemparseUser = {
  id?: string;
  email?: string;
  username?: string;
};

/** Event tags as string key/value pairs. */
export type SemparseTagMap = Record<string, string>;

/** Supported value shapes for context fields. */
export type SemparseContextValue =
  | string
  | number
  | boolean
  | null
  | Record<string, unknown>
  | unknown[];

/** Arbitrary structured context data for an event. */
export type SemparseContext = Record<string, SemparseContextValue>;

/** Standard HTTP request/response context fields. */
export type SemparseHttpContext = SemparseContext & {
  method?: string;
  url?: string;
  query_string?: string;
  fragment?: string;
  status_code?: number;
  request_id?: string;
};

/** Trace and span metadata associated with an event. */
export type SemparseTraceContext = SemparseContext & {
  trace_id?: string;
  span_id?: string;
  parent_span_id?: string;
  op?: string;
  status?: string;
  sampled?: boolean;
};

/** Session lifecycle metadata for an event. */
export type SemparseSessionContext = SemparseContext & {
  id?: string;
  status?: string;
  started_at?: string;
  duration_ms?: number;
};

/** Replay metadata used to correlate captured events. */
export type SemparseReplayContext = SemparseContext & {
  id?: string;
  url?: string;
  segment_id?: string;
};

/** Package and version metadata context. */
export type SemparsePackagesContext = SemparseContext & {
  package?: string;
  version?: string;
};

/** Loaded image metadata context. */
export type SemparseImagesContext = SemparseContext & {
  loaded?: SemparseContextValue[];
};

/** SDK identity metadata context. */
export type SemparseSdkContext = SemparseContext & {
  name?: string;
  version?: string;
};

/** Structured log entry context. */
export type SemparseLogsContext = SemparseContext & {
  entries?: SemparseContextValue[];
};

/** Named context buckets attached to an event payload. */
export type SemparseContextMap = {
  http?: SemparseHttpContext;
  trace?: SemparseTraceContext;
  session?: SemparseSessionContext;
  replay?: SemparseReplayContext;
  packages?: SemparsePackagesContext;
  dependencies?: SemparsePackagesContext;
  images?: SemparseImagesContext;
  sdk?: SemparseSdkContext;
  logs?: SemparseLogsContext;
  [key: string]: SemparseContext | undefined;
};

/** Breadcrumb recorded before capture to provide event timeline context. */
export type SemparseBreadcrumb = {
  timestamp?: string | number;
  type?: string;
  category?: string;
  level?: SemparseSeverity;
  message?: string;
  data?: Record<string, SemparseContextValue>;
};

type SemparseSdkInfo = {
  name: string;
  version: string;
};

type SemparseStacktrace = {
  raw?: string;
  frames?: SemparseStackFrame[];
};

type SemparseStackFrame = {
  filename?: string;
  abs_path?: string;
  function?: string;
  lineno?: number;
  colno?: number;
  in_app?: boolean;
};

type SemparseException = {
  type: string;
  value: string;
  stacktrace?: SemparseStacktrace;
};

type SemparseEvent = {
  event_id: string;
  timestamp: string;
  platform: string;
  level: SemparseSeverity;
  message?: string;
  fingerprint?: string[];
  exception?: {
    values: SemparseException[];
  };
  breadcrumbs?: SemparseBreadcrumb[];
  tags?: SemparseTagMap;
  contexts?: SemparseContextMap;
  user?: SemparseUser;
  release?: string;
  dist?: string;
  environment?: string;
  sdk?: SemparseSdkInfo;
};

type SemparseEnvelopeHeader = {
  event_id: string;
  sent_at: string;
  dsn: string;
  sdk?: SemparseSdkInfo;
};

type SemparseEnvelopeItemHeader = {
  type: "event";
  content_type: "application/json";
};

/** Outcome metadata from a single envelope send attempt. */
export type SemparseTransportResult = {
  ok: boolean;
  status: number;
};

/** Envelope transport contract used by the client. */
export type SemparseTransport = {
  send: (input: {
    dsn: string;
    envelope: string;
  }) => Promise<SemparseTransportResult>;
};

/** Capture response containing the emitted event id and transport status. */
export type SemparseCaptureResult = {
  eventId: string;
  transport: SemparseTransportResult;
};

type SemparseInitConfig = {
  dsn: string;
  ingestUrl?: string;
  release?: string;
  dist?: string;
  environment?: string;
  platform?: string;
  transport?: SemparseTransport;
  sdk?: SemparseSdkInfo;
  defaultTags?: SemparseTagMap;
  defaultContexts?: SemparseContextMap;
  defaultUser?: SemparseUser;
};

/** Public client API for capturing events and managing default scope data. */
export type SemparseClient = {
  captureException: (input: {
    error: unknown;
    tags?: SemparseTagMap;
    contexts?: SemparseContextMap;
    breadcrumbs?: SemparseBreadcrumb[];
    user?: SemparseUser;
    level?: SemparseSeverity;
    fingerprint?: string[];
  }) => Promise<SemparseCaptureResult>;
  captureMessage: (input: {
    message: string;
    tags?: SemparseTagMap;
    contexts?: SemparseContextMap;
    breadcrumbs?: SemparseBreadcrumb[];
    user?: SemparseUser;
    level?: SemparseSeverity;
    fingerprint?: string[];
  }) => Promise<SemparseCaptureResult>;
  setUser: (input: { user?: SemparseUser }) => void;
  setTag: (input: { key: string; value: string }) => void;
  setTags: (input: { tags: SemparseTagMap }) => void;
  setContext: (input: { key: string; context: SemparseContext }) => void;
  flush: (input: { timeoutMs?: number }) => Promise<void>;
};

type SemparseTransportConfig = {
  dsn: string;
  ingestUrl?: string;
  fetch?: typeof fetch;
  sdk?: SemparseSdkInfo;
};

type SemparseDsn = {
  dsn: string;
  protocol: string;
  host: string;
  projectId: string;
  publicKey: string;
  ingestUrl: string;
};

type SemparseReleaseInput = {
  appVersion: string;
  buildNumber: string;
  updateId?: string;
};

type SemparseReleaseInfo = {
  release: string;
  dist?: string;
};

let activeClient: SemparseClient | undefined;

/** Parses a DSN and derives the default envelope ingest URL. */
export const parseDsn = (input: { dsn: string }): SemparseDsn => {
  const url = new URL(input.dsn);
  const pathSegments = url.pathname
    .split("/")
    .filter((segment) => segment.length > 0);
  const projectId = pathSegments.at(-1) ?? "";
  const publicKey = url.username;
  const ingestUrl = `${url.protocol}//${url.host}/api/${projectId}/envelope`;

  return {
    dsn: input.dsn,
    protocol: url.protocol,
    host: url.host,
    projectId,
    publicKey,
    ingestUrl,
  };
};

/** Creates an HTTP transport that posts serialized envelopes to Semparse ingest. */
export const createTransport = (
  input: SemparseTransportConfig,
): SemparseTransport => {
  const fetcher =
    input.fetch ??
    (typeof globalThis.fetch === "function" ? globalThis.fetch : undefined);
  const dsn = parseDsn({ dsn: input.dsn });

  return {
    send: async (request) => {
      if (!fetcher) {
        return { ok: false, status: 0 };
      }

      const ingestUrl = new URL(input.ingestUrl ?? dsn.ingestUrl);
      ingestUrl.searchParams.set("dsn", request.dsn);

      const headers: Record<string, string> = {
        "Content-Type": "text/plain;charset=UTF-8",
      };

      const response = await fetcher(ingestUrl.toString(), {
        method: "POST",
        headers,
        body: request.envelope,
      });

      return { ok: response.ok, status: response.status };
    },
  };
};

/** Builds release and dist values from app version metadata. */
export const formatRelease = (
  input: SemparseReleaseInput,
): SemparseReleaseInfo => {
  const release = `${input.appVersion}+${input.buildNumber}`;
  const dist = input.updateId ?? input.buildNumber;

  return { release, dist };
};

/** Initializes and stores the process-wide active Semparse client. */
export const init = (config: SemparseInitConfig): SemparseClient => {
  const client = createClient(config);
  activeClient = client;
  return client;
};

/**
 * Captures an exception through the active client.
 * Returns `undefined` when the SDK has not been initialized.
 */
export const captureException = async (input: {
  error: unknown;
  tags?: SemparseTagMap;
  contexts?: SemparseContextMap;
  breadcrumbs?: SemparseBreadcrumb[];
  user?: SemparseUser;
  level?: SemparseSeverity;
}): Promise<SemparseCaptureResult | undefined> => {
  if (!activeClient) {
    return undefined;
  }

  return activeClient.captureException(input);
};

/**
 * Captures a message through the active client.
 * Returns `undefined` when the SDK has not been initialized.
 */
export const captureMessage = async (input: {
  message: string;
  tags?: SemparseTagMap;
  contexts?: SemparseContextMap;
  breadcrumbs?: SemparseBreadcrumb[];
  user?: SemparseUser;
  level?: SemparseSeverity;
}): Promise<SemparseCaptureResult | undefined> => {
  if (!activeClient) {
    return undefined;
  }

  return activeClient.captureMessage(input);
};

/** Sets the default user scope on the active client. */
export const setUser = (input: { user?: SemparseUser }): void => {
  if (!activeClient) {
    return;
  }

  activeClient.setUser(input);
};

/** Sets or overwrites a single default tag on the active client. */
export const setTag = (input: { key: string; value: string }): void => {
  if (!activeClient) {
    return;
  }

  activeClient.setTag(input);
};

/** Merges default tags into the active client scope. */
export const setTags = (input: { tags: SemparseTagMap }): void => {
  if (!activeClient) {
    return;
  }

  activeClient.setTags(input);
};

/** Sets a named default context on the active client scope. */
export const setContext = (input: {
  key: string;
  context: SemparseContext;
}): void => {
  if (!activeClient) {
    return;
  }

  activeClient.setContext(input);
};

/** Flushes pending sends for the active client, when initialized. */
export const flush = async (input?: { timeoutMs?: number }): Promise<void> => {
  if (!activeClient) {
    return;
  }
  await activeClient.flush(input ?? {});
};

const createClient = (config: SemparseInitConfig): SemparseClient => {
  const sdk = config.sdk ?? DEFAULT_SDK;
  const transport =
    config.transport ??
    createTransport({
      dsn: config.dsn,
      sdk,
      ingestUrl: config.ingestUrl,
    });
  const base = {
    dsn: config.dsn,
    release: config.release ?? "unknown",
    dist: config.dist,
    environment: config.environment ?? "production",
    platform: config.platform ?? "javascript",
    sdk,
    transport,
  };

  let tags: SemparseTagMap = { ...config.defaultTags };
  let contexts: SemparseContextMap = {
    ...config.defaultContexts,
  };
  let user: SemparseUser | undefined = config.defaultUser;
  const sessionId = createHexId(16);
  const sessionStartedAt = new Date().toISOString();

  const buildDefaultSectionContexts = (input: {
    eventId: string;
    level: SemparseSeverity;
    message?: string;
    breadcrumbs?: SemparseBreadcrumb[];
  }): SemparseContextMap | undefined => {
    const currentUrl = resolveCurrentUrl();
    const trace: SemparseTraceContext = {
      trace_id: createHexId(32),
      span_id: createHexId(16),
      op: "error.capture",
      status:
        input.level === "error" || input.level === "fatal"
          ? "internal_error"
          : "ok",
      event_id: input.eventId,
    };
    if (currentUrl) {
      trace.transaction = currentUrl;
    }

    const session: SemparseSessionContext = {
      id: sessionId,
      started_at: sessionStartedAt,
      status: "active",
    };

    const replay: SemparseReplayContext = {
      id: sessionId,
    };
    if (currentUrl) {
      replay.url = currentUrl;
    }

    const http: SemparseHttpContext | undefined = currentUrl
      ? {
          method: "GET",
          url: currentUrl,
        }
      : undefined;

    const packages: SemparsePackagesContext = {
      package: base.sdk.name,
      version: base.sdk.version,
    };

    const sdkContext: SemparseSdkContext = {
      name: base.sdk.name,
      version: base.sdk.version,
    };

    const logEntries: SemparseContextValue[] = [];
    for (const breadcrumb of input.breadcrumbs ?? []) {
      const entry: SemparseContext = {};
      if (breadcrumb.message) {
        entry.message = breadcrumb.message;
      }
      if (breadcrumb.level) {
        entry.level = breadcrumb.level;
      }
      if (breadcrumb.category) {
        entry.category = breadcrumb.category;
      }
      if (breadcrumb.type) {
        entry.type = breadcrumb.type;
      }
      if (breadcrumb.timestamp !== undefined) {
        entry.timestamp =
          typeof breadcrumb.timestamp === "number"
            ? breadcrumb.timestamp
            : breadcrumb.timestamp;
      }
      if (breadcrumb.data) {
        entry.data = breadcrumb.data;
      }
      if (Object.keys(entry).length > 0) {
        logEntries.push(entry);
      }
    }

    if (logEntries.length === 0 && input.message) {
      logEntries.push({
        message: input.message,
        level: input.level,
      });
    }

    const logs: SemparseLogsContext | undefined =
      logEntries.length > 0 ? { entries: logEntries } : undefined;

    const loadedImages = collectLoadedImages();
    const images: SemparseImagesContext | undefined =
      loadedImages.length > 0 ? { loaded: loadedImages } : undefined;

    return mergeContextMaps(
      http ? { http } : undefined,
      { trace },
      { session },
      { replay },
      { packages },
      { sdk: sdkContext },
      logs ? { logs } : undefined,
      images ? { images } : undefined,
    );
  };

  const sendEvent = async (
    event: SemparseEvent,
  ): Promise<SemparseCaptureResult> => {
    const envelope = serializeEnvelope({ dsn: base.dsn, event, sdk: base.sdk });
    const transportResult = await base.transport
      .send({ dsn: base.dsn, envelope })
      .catch(() => ({ ok: false, status: 0 }));

    return { eventId: event.event_id, transport: transportResult };
  };

  const buildEvent = (input: {
    level: SemparseSeverity;
    message?: string;
    exception: SemparseException;
    eventTags?: SemparseTagMap;
    eventContexts?: SemparseContextMap;
    breadcrumbs?: SemparseBreadcrumb[];
    eventUser?: SemparseUser;
    fingerprint?: string[];
  }): SemparseEvent => {
    const eventId = createEventId();
    const mergedTags = mergeTags(tags, input.eventTags);
    const mergedContexts = mergeContextMaps(
      contexts,
      buildDefaultSectionContexts({
        eventId,
        level: input.level,
        message: input.message,
        breadcrumbs: input.breadcrumbs,
      }),
      input.eventContexts,
    );
    const mergedUser = input.eventUser ?? user;

    return {
      event_id: eventId,
      timestamp: new Date().toISOString(),
      platform: base.platform,
      level: input.level,
      message: input.message,
      fingerprint: input.fingerprint,
      exception: {
        values: [input.exception],
      },
      breadcrumbs: input.breadcrumbs,
      tags: mergedTags,
      contexts: mergedContexts,
      user: mergedUser,
      release: base.release,
      dist: base.dist,
      environment: base.environment,
      sdk: base.sdk,
    };
  };

  return {
    captureException: async (input) => {
      const exception = normalizeException(input.error);
      const event = buildEvent({
        level: input.level ?? "error",
        exception,
        eventTags: input.tags,
        eventContexts: input.contexts,
        breadcrumbs: input.breadcrumbs,
        eventUser: input.user,
        fingerprint: input.fingerprint,
      });

      return sendEvent(event);
    },
    captureMessage: async (input) => {
      const exception = {
        type: "Message",
        value: input.message,
      };
      const event = buildEvent({
        level: input.level ?? "info",
        message: input.message,
        exception,
        eventTags: input.tags,
        eventContexts: input.contexts,
        breadcrumbs: input.breadcrumbs,
        eventUser: input.user,
        fingerprint: input.fingerprint,
      });

      return sendEvent(event);
    },
    setUser: (input) => {
      user = input.user;
    },
    setTag: (input) => {
      tags = { ...tags, [input.key]: input.value };
    },
    setTags: (input) => {
      tags = { ...tags, ...input.tags };
    },
    setContext: (input) => {
      contexts = { ...contexts, [input.key]: input.context };
    },
    flush: async () => {},
  };
};

const serializeEnvelope = (input: {
  dsn: string;
  event: SemparseEvent;
  sdk: SemparseSdkInfo | undefined;
}): string => {
  const header: SemparseEnvelopeHeader = {
    event_id: input.event.event_id,
    sent_at: new Date().toISOString(),
    dsn: input.dsn,
    sdk: input.sdk,
  };
  const itemHeader: SemparseEnvelopeItemHeader = {
    type: "event",
    content_type: "application/json",
  };

  return `${JSON.stringify(header)}\n${JSON.stringify(itemHeader)}\n${JSON.stringify(input.event)}`;
};

const STACK_FRAME_PATTERN = /^\s*at\s+(?:(.*?)\s+\()?(.+?):(\d+):(\d+)\)?$/;

const normalizeFramePath = (input: string): string => {
  const normalized = input.replaceAll("\\", "/").trim();
  const noHash = normalized.split("#")[0] ?? normalized;
  const noQuery = noHash.split("?")[0] ?? noHash;
  const hermesDelimiterIndex = noQuery.indexOf("//&");
  if (hermesDelimiterIndex !== -1) {
    return noQuery.slice(0, hermesDelimiterIndex);
  }
  return noQuery;
};

const canParseUrl = (input: string): boolean => {
  if (typeof URL.canParse === "function") {
    return URL.canParse(input);
  }
  return /^(https?:\/\/|file:\/\/)/i.test(input);
};

const parseStackFrameLine = (
  input: string,
): SemparseStackFrame | undefined => {
  const match = input.match(STACK_FRAME_PATTERN);
  if (!match) {
    return undefined;
  }

  const rawFunctionName = match[1]?.trim();
  const rawLocation = match[2]?.trim();
  const rawLine = match[3];
  const rawColumn = match[4];

  if (!rawLocation || !rawLine || !rawColumn) {
    return undefined;
  }

  const lineno = Number.parseInt(rawLine, 10);
  const colno = Number.parseInt(rawColumn, 10);
  if (!Number.isFinite(lineno) || !Number.isFinite(colno)) {
    return undefined;
  }

  let filename = normalizeFramePath(rawLocation);
  let absPath: string | undefined;

  if (canParseUrl(rawLocation)) {
    const parsed = new URL(rawLocation);
    absPath = normalizeFramePath(parsed.toString());
    filename = normalizeFramePath(
      parsed.pathname.length > 0 ? parsed.pathname : absPath,
    );
  }

  if (filename.length === 0) {
    return undefined;
  }

  const lower = filename.toLowerCase();
  const inApp =
    !lower.startsWith("node:") &&
    !lower.startsWith("internal/") &&
    !lower.includes("/node_modules/") &&
    !lower.includes("[native code]");

  return {
    filename,
    abs_path: absPath,
    function: rawFunctionName && rawFunctionName.length > 0
      ? rawFunctionName
      : undefined,
    lineno,
    colno,
    in_app: inApp,
  };
};

const parseStackFrames = (
  stack: string | undefined,
): SemparseStackFrame[] | undefined => {
  if (!stack || stack.length === 0) {
    return undefined;
  }

  const normalizedStack = truncateText(stack, MAX_STACKTRACE_INPUT_LENGTH);
  if (!normalizedStack) {
    return undefined;
  }

  const frames: SemparseStackFrame[] = [];
  for (const line of normalizedStack.split("\n")) {
    const frame = parseStackFrameLine(line);
    if (frame) {
      frames.push({
        ...frame,
        filename: truncateText(frame.filename, MAX_STACKTRACE_STRING_FIELD_LENGTH),
        abs_path: truncateText(frame.abs_path, MAX_STACKTRACE_STRING_FIELD_LENGTH),
        function: truncateText(frame.function, MAX_STACKTRACE_STRING_FIELD_LENGTH),
      });
      if (frames.length >= MAX_STACKTRACE_FRAMES) {
        break;
      }
    }
  }

  return frames.length > 0 ? frames : undefined;
};

const normalizeException = (error: unknown): SemparseException => {
  if (error instanceof Error) {
    const normalizedStack = truncateText(
      error.stack,
      MAX_STACKTRACE_INPUT_LENGTH,
    );
    const stacktrace = error.stack
      ? {
          raw: undefined,
          frames: parseStackFrames(normalizedStack),
        }
      : undefined;
    return {
      type: truncateText(error.name, MAX_EXCEPTION_TYPE_LENGTH) ?? "Error",
      value:
        truncateText(error.message, MAX_EXCEPTION_VALUE_LENGTH) ??
        "Unknown error",
      stacktrace,
    };
  }

  if (typeof error === "string") {
    return {
      type: "Error",
      value: truncateText(error, MAX_EXCEPTION_VALUE_LENGTH) ?? "Unknown error",
    };
  }

  if (error && typeof error === "object") {
    if ("message" in error && typeof error.message === "string") {
      return {
        type: "Error",
        value:
          truncateText(error.message, MAX_EXCEPTION_VALUE_LENGTH) ??
          "Unknown error",
      };
    }

    return {
      type: "Error",
      value:
        truncateText(JSON.stringify(error), MAX_EXCEPTION_VALUE_LENGTH) ??
        "Unknown error",
    };
  }

  return { type: "Error", value: "Unknown error" };
};

const truncateText = (
  value: string | undefined,
  maxLength: number,
): string | undefined => {
  if (typeof value !== "string") {
    return value;
  }

  if (value.length <= maxLength) {
    return value;
  }

  const truncationLimit = Math.max(0, maxLength - TRUNCATION_SUFFIX.length);
  return `${value.slice(0, truncationLimit)}${TRUNCATION_SUFFIX}`;
};

const mergeTags = (
  baseTags: SemparseTagMap,
  nextTags: SemparseTagMap | undefined,
): SemparseTagMap | undefined => {
  const merged = { ...baseTags, ...nextTags };
  return Object.keys(merged).length > 0 ? merged : undefined;
};

const mergeContextMaps = (
  ...contextMaps: Array<SemparseContextMap | undefined>
): SemparseContextMap | undefined => {
  let merged: SemparseContextMap = {};
  for (const contextMap of contextMaps) {
    if (!contextMap) {
      continue;
    }
    merged = { ...merged, ...contextMap };
  }
  return Object.keys(merged).length > 0 ? merged : undefined;
};

const toObjectRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (typeof value !== "object" || value === null) {
    return undefined;
  }

  const record: Record<string, unknown> = {};
  for (const [key, entryValue] of Object.entries(value)) {
    record[key] = entryValue;
  }
  return record;
};

const resolveCurrentUrl = (): string | undefined => {
  const globalObject = toObjectRecord(globalThis);
  const locationValue = toObjectRecord(globalObject?.["location"]);
  const href = locationValue?.["href"];
  if (typeof href !== "string") {
    return undefined;
  }
  const trimmed = href.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const collectLoadedImages = (): SemparseContext[] => {
  const performanceValue = globalThis.performance;
  if (!performanceValue || typeof performanceValue.getEntriesByType !== "function") {
    return [];
  }

  const resourceEntries = performanceValue.getEntriesByType("resource");
  if (!Array.isArray(resourceEntries)) {
    return [];
  }

  const loadedImages: SemparseContext[] = [];
  for (const entry of resourceEntries) {
    const entryRecord = toObjectRecord(entry);
    if (!entryRecord) {
      continue;
    }

    const initiatorType =
      typeof entryRecord["initiatorType"] === "string"
        ? entryRecord["initiatorType"]
        : undefined;
    if (initiatorType !== "img" && initiatorType !== "image") {
      continue;
    }

    const name =
      typeof entryRecord["name"] === "string" ? entryRecord["name"] : undefined;
    if (!name || name.trim().length === 0) {
      continue;
    }

    const image: SemparseContext = {
      url: name.trim(),
    };

    const transferSize = entryRecord["transferSize"];
    if (typeof transferSize === "number" && Number.isFinite(transferSize)) {
      image.transfer_size = Math.trunc(transferSize);
    }
    const duration = entryRecord["duration"];
    if (typeof duration === "number" && Number.isFinite(duration)) {
      image.duration_ms = Math.trunc(duration);
    }

    loadedImages.push(image);
    if (loadedImages.length >= 20) {
      break;
    }
  }

  return loadedImages;
};

const createHexId = (length: number): string => {
  let value = "";
  while (value.length < length) {
    value += Math.floor(Math.random() * 0xFF_FF_FF_FF)
      .toString(16)
      .padStart(8, "0");
  }
  return value.slice(0, length);
};

const createEventId = (): string => {
  const timestamp = Date.now().toString(16);
  const random = Math.random().toString(16).slice(2, 10);
  return `${timestamp}${random}`;
};
